import {
  dayjs,
  IncidentWorkItemStatusSchema,
  type Dayjs,
} from "@thalamos/common";

import { IncidentTypeSchema } from "../types/incidentType.js";
import type { IncidentWorkItemWithContext } from "../types/incidentWorkItem.js";
import { calculateIncidentWorkItemStatus } from "./calculateIncidentWorkItemStatus.js";
import { getEarliestPlaceOfSafetyArrivalDateTime } from "./getEarliestPlaceOfSafetyArrivalDateTime.js";
import { getPoliceLeftDateTime } from "./getPoliceLeftDateTime.js";
import { getIncidentTypeSelection } from "./helpers/getIncidentTypeSelection.js";

export type TimeAtPlaceOfSafety = {
  minutes: number;
  /** Police have not yet left, so the time runs up to `now`. */
  isOngoing: boolean;
};

/**
 * How long police have spent at places of safety on a Section 136 incident: from the
 * earliest arrival until police left, or until `now` while the incident is still open.
 * `undefined` when there is nothing to measure — another incident type, no arrival yet,
 * or an incident that ended without a recorded time police left.
 */
export function getTimeAtPlaceOfSafety(
  incident: IncidentWorkItemWithContext,
  now: Dayjs,
): TimeAtPlaceOfSafety | undefined {
  const isSection136 =
    getIncidentTypeSelection({ incidentWorkItem: incident }).unwrapOr(
      undefined,
    ) === IncidentTypeSchema.enum.section136;
  const arrival = getEarliestPlaceOfSafetyArrivalDateTime(incident);
  if (!isSection136 || arrival === undefined) {
    return undefined;
  }

  const left = getPoliceLeftDateTime(incident);
  if (left !== undefined) {
    return {
      minutes: dayjs(left).diff(dayjs(arrival), "minute"),
      isOngoing: false,
    };
  }

  const isEnded =
    calculateIncidentWorkItemStatus(incident).unwrapOr(
      IncidentWorkItemStatusSchema.enum.unknown,
    ) === IncidentWorkItemStatusSchema.enum.ended;
  if (isEnded) {
    return undefined;
  }

  return { minutes: now.diff(dayjs(arrival), "minute"), isOngoing: true };
}
